import type { MouseEvent, ReactElement } from "react";

import { Dialog } from "../../atoms/Dialog";
import { useConfirmDialogContext } from "./ConfirmDialogContext";

export interface ConfirmDialogTriggerProps {
  /** The element that opens the dialog. It keeps its own markup and handlers. */
  children: ReactElement;
}

/**
 * Opens the dialog when its child is clicked, so the dialog manages its own open state. Place it
 * anywhere inside `ConfirmDialog`. Optional: leave it out and pass `open`/`onOpenChange` when
 * something other than one control decides to ask, such as a dropdown menu item or a navigation
 * guard.
 */
export const ConfirmDialogTrigger = ({ children }: ConfirmDialogTriggerProps) => {
  const { openFromTrigger } = useConfirmDialogContext();

  // Triggers often sit inside clickable rows, cards or links, which must not also activate, so the
  // click is stopped and its default (following a link's href) cancelled. Radix skips its own open
  // toggle once the default is prevented, so the dialog is opened here instead.
  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    openFromTrigger();
  };

  return (
    <Dialog.Trigger asChild onClick={handleClick}>
      {children}
    </Dialog.Trigger>
  );
};

ConfirmDialogTrigger.displayName = "ConfirmDialog.Trigger";
